import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, CheckCircle2, Share, Plus } from "lucide-react";
import {
  canInstall,
  subscribeInstall,
  triggerInstall,
  isStandalone,
  isIOS,
} from "@/lib/pwaInstall";

export function AppInstallSettings() {
  const [installable, setInstallable] = useState(canInstall());
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const update = () => {
      setInstallable(canInstall());
      setInstalled(isStandalone());
    };
    update();
    return subscribeInstall(update);
  }, []);

  const handleInstall = useCallback(async () => {
    const outcome = await triggerInstall();
    if (outcome === "accepted") setInstalled(true);
    setInstallable(canInstall());
  }, []);

  const ios = isIOS();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          App installieren
        </CardTitle>
        <CardDescription>
          Installieren Sie R-NetWork CRM auf dem Startbildschirm – schneller Zugriff wie eine echte App, auch offline nutzbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {installed ? (
          <div
            data-testid="app-install-already"
            className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg p-4"
          >
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">Die App ist bereits installiert.</p>
          </div>
        ) : ios ? (
          <div data-testid="app-install-ios" className="bg-muted/50 rounded-lg p-4 text-sm space-y-2">
            <p className="font-medium">So installieren Sie die App auf dem iPhone/iPad:</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li className="flex items-center gap-1 flex-wrap">
                Tippen Sie in Safari auf <Share className="inline w-4 h-4" /> „Teilen".
              </li>
              <li className="flex items-center gap-1 flex-wrap">
                Wählen Sie <Plus className="inline w-4 h-4" /> „Zum Home-Bildschirm".
              </li>
              <li>Bestätigen Sie mit „Hinzufügen".</li>
            </ol>
          </div>
        ) : installable ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="R-NetWork CRM" className="w-12 h-12 rounded-xl flex-shrink-0" />
              <div className="text-sm text-muted-foreground">
                R-NetWork CRM auf dem Startbildschirm – wie eine echte App.
              </div>
            </div>
            <Button onClick={handleInstall} className="gap-2" data-testid="app-install-btn">
              <Download className="w-4 h-4" />
              Jetzt installieren
            </Button>
          </div>
        ) : (
          <div data-testid="app-install-unavailable" className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground space-y-2">
            <p>
              Die Installation wird von diesem Browser gerade nicht angeboten. Öffnen Sie die App in
              <span className="font-medium text-foreground"> Google Chrome</span> (Android/Desktop) oder
              <span className="font-medium text-foreground"> Safari</span> (iPhone/iPad), um sie zu installieren.
            </p>
            <p>Falls die App bereits installiert ist, erscheint diese Option nicht mehr.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AppInstallSettings;
